import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { animated, to, useSpring, SpringRef } from '@react-spring/web';
import _isEqual from 'lodash/isEqual';
import TextEditor from '@src/components/wysiwyg_editor/TextEditor';
import { PositionableTextDocument } from '@src/lib/PositionableTextDocument';
import { stylesToDefaultMarkValues } from '@src/utils/wysiwyg_editor/styles';
import { Portal } from '@src/components/Portal';
import TextOverflowIndicator from '@src/components/stencils/TextOverflowIndicator';
import { calculateOverflowContainerLocation, calculateTextOverflows } from '@src/utils/wysiwyg_editor/overflow';
import useDomLocationInWysiwygContainer from '@src/hooks/wysiwyg_editor/useDomLocationInWysiwygContainer';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import AddTextButton from '@src/components/wysiwyg_editor/page_content_items/text/AddTextButton';
import DragFrame from '@src/components/wysiwyg_editor/page_content_items/DragFrame';
import { calculateLocations } from '../../../../utils/wysiwyg_editor/contentItemHandleLocations';

import { emptyDocument } from '@src/utils/proseMirror';

import type { TTextBoxContentItem } from '@src/types/wysiwyg_editor';
import type { SprungRect, TRect } from '../../../../types/misc';
import type {
  TOverlayLocations,
  TSprungOverlayLocations
} from '../../../../utils/wysiwyg_editor/contentItemHandleLocations';
import { handleElementMovement, MINIMUM_DIMENSION, SNAP_DISTANCE } from '@src/utils/wysiwyg_editor/elementMovement';
import ContentDragShield from '@src/components/wysiwyg_editor/page_content_items/ContentDragShield';
import { HANDLE_POSITIONS } from '@src/components/wysiwyg_editor/page_content_items/DragHandle';
import useOnClickOutside from 'use-onclickoutside';
import ControlPanel from '@src/components/wysiwyg_editor/page_content_items/text/ControlPanel';
import { TextEditorDispatchContext } from '@src/contexts/wysiwyg/text_editor';
import { EditorView } from 'prosemirror-view';
import ErrorBoundary from '@src/components/ErrorBoundary';
import { defaultMarkValues } from '@src/prose_mirror/schemas/TextSchema';
import usePrevious from '@src/hooks/usePrevious';
import useIsLargeScreen from '@src/hooks/useIsLargeScreen';
import { isIOS, isMobile } from 'react-device-detect';
import useSnappableLocations from '../../../../hooks/wysiwyg_editor/useSnappableLocations';
import SnapLines from '../../SnapLines';
import { WysiwygEditorDispatchContext, WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import { PageContentDispatchContext } from '@src/contexts/wysiwyg/page_content';
import usePageContentItemStackOrder from '@src/hooks/wysiwyg_editor/usePageContentItemStackOrder';
import {
  saveSnapshot,
  updateContentItemContainerLocation,
  updateContentItemContentLocation
} from '@src/actions/wysiwyg/editor';
import Preview from './Preview';
import KeyBindingDelete from '../../key_bindings/delete';
import usePreviousUnique from '@src/hooks/usePreviousUnique';
import _pick from 'lodash/pick';

const overflowIndicatorLocation = {
  top: 0,
  right: -35
};

const DISPLAY_STATES = {
  render: 'RENDER',
  move: 'MOVE',
  moving: 'MOVING',
  resizing: 'RESIZING',
  edit: 'EDIT'
};

const FOCUSED_STATES = Object.values(_pick(DISPLAY_STATES, ['move', 'moving', 'resizing', 'edit']));

const STACK_ORDER_GROUPINGS = {
  staticRenderedText: 1,
  handles: 100,
  overflowIndicator: 100,
  activeHandles: 200,
  controlPanel: 300
};

interface Props {
  data: TTextBoxContentItem;
  frameDomSpringValues: TSprungOverlayLocations;
  frameDomSpringApi: SpringRef<TOverlayLocations>;
  frameViewboxSpringValues: SprungRect;
  frameViewboxSpringApi: SpringRef<TRect>;
}

function Builder(props: Props): React.ReactElement {
  const { data, frameDomSpringValues, frameDomSpringApi, frameViewboxSpringValues, frameViewboxSpringApi } = props;
  const dispatch = useDispatch();
  const isLarge = useIsLargeScreen();
  const [textOverflows, setTextOverflows] = useState(false);
  const [displayState, setDisplayState] = useState(DISPLAY_STATES.render);
  const prevDisplayState = usePrevious(displayState);
  const [isHovering, setIsHovering] = useState(false);
  const [isBeingDragged, setIsBeingDragged] = useState(false);
  const stackOrder = usePageContentItemStackOrder(data.id);
  const snappableLocations = useSnappableLocations(data.id);

  const { setActiveView, setTextEditorContainerFocused } = useContext(TextEditorDispatchContext);
  const {
    activeToolboxTab: activeTab,
    productionRender,
    renderableDomLocation,
    renderRect: stencilRenderRect,
    renderableArea: renderableViewboxArea,
    svgRef,
    viewbox
  } = useContext(WysiwygEditorStateContext);
  const { toggleToolboxTab } = useContext(WysiwygEditorDispatchContext);
  const { setSelectedPageContentItemId } = useContext(PageContentDispatchContext);
  const [textDocument, setTextDocument] = useState(() =>
    data.content
      ? PositionableTextDocument.fromPositionedDocument(data.content)
      : emptyDocument(data.styles?.align, stylesToDefaultMarkValues(defaultMarkValues, data.styles))
  );
  const [textContent, setTextContent] = useState(() => textDocument.textContent);

  const [hasMoved, setHasMoved] = useState(false);
  const [displayStateBeforeDrag, setDisplayStateBeforeDrag] = useState(displayState);

  const containerLocationInDom = useDomLocationInWysiwygContainer(data.containerLocation);
  const contentLocationInDom = useDomLocationInWysiwygContainer(data.contentLocation);

  const [contentDomSpringValues, contentDomSpringApi] = useSpring<TRect>(
    () => ({
      config: { duration: 1 },
      ...contentLocationInDom
    }),
    [contentLocationInDom]
  );

  const previousDisplayState = usePreviousUnique(displayState);

  const domElementsRef = useRef<HTMLElement | null>(null);
  const renderPointRef = useRef<HTMLDivElement | null>(null);
  const initialDragLocationRef = useRef(data.containerLocation);
  const initialSnappableLocationsRef = useRef(snappableLocations);

  const handleDragStart = useCallback(() => {
    initialDragLocationRef.current = { ...data.containerLocation };
    initialSnappableLocationsRef.current = snappableLocations;
    setDisplayStateBeforeDrag(displayState);
    setIsBeingDragged(true);
  }, [data.containerLocation, displayState, snappableLocations]);

  const handleDragStop = useCallback(() => {
    setDisplayState(
      hasMoved || displayStateBeforeDrag === DISPLAY_STATES.render ? DISPLAY_STATES.move : DISPLAY_STATES.edit
    );
    setHasMoved(false);
    setIsBeingDragged(false);
  }, [displayStateBeforeDrag, hasMoved]);

  const handleDrag = useCallback(
    (position: string, movementInPixels: [number, number], _isFirst: boolean, isLast: boolean) => {
      if (!initialDragLocationRef.current) return;
      if (!renderableDomLocation) return;
      if (!stencilRenderRect) return;

      if (movementInPixels[0] || movementInPixels[1]) {
        setHasMoved(true);

        if (position === HANDLE_POSITIONS.inner) {
          setDisplayState(DISPLAY_STATES.moving);
        } else {
          setDisplayState(DISPLAY_STATES.resizing);
        }
      }

      const movementPercentages = [
        movementInPixels[0] / stencilRenderRect.width,
        movementInPixels[1] / stencilRenderRect.height
      ];

      const newLocation = handleElementMovement(
        position,
        initialDragLocationRef.current,
        movementPercentages,
        renderableViewboxArea,
        {
          height: MINIMUM_DIMENSION / stencilRenderRect.height,
          width: MINIMUM_DIMENSION / stencilRenderRect.width
        },
        initialSnappableLocationsRef.current,
        {
          x: SNAP_DISTANCE / stencilRenderRect.width,
          y: SNAP_DISTANCE / stencilRenderRect.height
        }
      );

      if (isLast) {
        if (!_isEqual(newLocation, initialDragLocationRef.current)) {
          dispatch(saveSnapshot());
          dispatch(updateContentItemContainerLocation(data.id, newLocation));
          dispatch(
            updateContentItemContentLocation(data.id, {
              height: newLocation.height,
              width: newLocation.width,
              x: 0,
              y: 0
            })
          );
        }
      } else {
        const newContentLocationInPixels = {
          x: 0,
          y: 0,
          height: newLocation.height * stencilRenderRect.height,
          width: newLocation.width * stencilRenderRect.width
        };
        contentDomSpringApi(newContentLocationInPixels);

        const newContainerLocationInViewboxPixels = {
          x: newLocation.x * viewbox.width,
          y: newLocation.y * viewbox.height,
          height: newLocation.height * viewbox.height,
          width: newLocation.width * viewbox.width
        };
        const newContainerLocationInPixels = {
          x: newLocation.x * stencilRenderRect.width,
          y: newLocation.y * stencilRenderRect.height,
          height: newLocation.height * stencilRenderRect.height,
          width: newLocation.width * stencilRenderRect.width
        };
        frameDomSpringApi(calculateLocations(newContainerLocationInPixels));
        frameViewboxSpringApi(newContainerLocationInViewboxPixels);
      }
    },
    [
      contentDomSpringApi,
      data.id,
      dispatch,
      frameDomSpringApi,
      frameViewboxSpringApi,
      renderableDomLocation,
      renderableViewboxArea,
      stencilRenderRect,
      viewbox.height,
      viewbox.width
    ]
  );

  const handleViewMount = useCallback(
    (view: EditorView) => {
      setActiveView(view);
    },
    [setActiveView]
  );

  const setDisplayStateRender = useCallback(() => {
    setDisplayState(DISPLAY_STATES.render);
  }, []);

  const setDisplayStateMove = useCallback(() => {
    setDisplayState(DISPLAY_STATES.move);
  }, []);

  useOnClickOutside(domElementsRef, (e: MouseEvent) => {
    const isTextCacheItem = $(e.target).closest('.wysiwyg-editor-drawer-textcache-item').length > 0;
    if (!isTextCacheItem) {
      if ($(e.target).closest('.wysiwyg-editor-toolbox-drawer-text').length) {
        return;
      }
      if ($(e.target).closest('.wysiwyg-editor-toolbox-buttons button').length) {
        return;
      }
    }

    setDisplayStateRender();
  });

  useEffect(() => {
    const handleBlur = setDisplayStateRender;
    const svg = svgRef.current;

    svg.addEventListener('blur-content-items', handleBlur);

    return () => {
      svg.removeEventListener('blur-content-items', handleBlur);
    };
  }, [setDisplayStateRender, svgRef]);

  useEffect(() => {
    setTextEditorContainerFocused(displayState === DISPLAY_STATES.move);
  }, [displayState, setTextEditorContainerFocused]);

  useEffect(() => {
    if (FOCUSED_STATES.includes(displayState) && !FOCUSED_STATES.includes(previousDisplayState)) {
      setSelectedPageContentItemId(data.id);
    }
  }, [displayState, data.id, setSelectedPageContentItemId, previousDisplayState]);

  useEffect(() => {
    const newTextDocument = data.content
      ? PositionableTextDocument.fromPositionedDocument(data.content)
      : emptyDocument(data.styles?.align, stylesToDefaultMarkValues(defaultMarkValues, data.styles));

    setTextDocument(newTextDocument);
    setTextContent(newTextDocument.textContent);
  }, [data.content, data.styles]);

  useEffect(() => {
    setTextOverflows(calculateTextOverflows(textDocument, data.contentLocation.height, data.contentLocation.width));
  }, [data.contentLocation.height, data.contentLocation.width, setTextOverflows, textDocument]);

  useEffect(() => {
    if (activeTab === 'text') return;
    if (displayState !== DISPLAY_STATES.edit) return;
    if (prevDisplayState === displayState) return;

    if (isLarge && !isIOS) toggleToolboxTab('text');
  }, [activeTab, dispatch, displayState, isLarge, prevDisplayState, toggleToolboxTab]);

  if (!stencilRenderRect) return null;

  const handlesAreActive = displayState !== DISPLAY_STATES.render && !isHovering;

  return (
    <Portal htmlId="wysiwyg-editor-portal" ref={domElementsRef}>
      {textContent && displayState === DISPLAY_STATES.render ? (
        <animated.svg
          height={frameViewboxSpringValues.height}
          width={frameViewboxSpringValues.width}
          viewBox={to([frameViewboxSpringValues.height, frameViewboxSpringValues.width], (h, w) => `0 0 ${w} ${h}`)}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            position: 'absolute',
            top: frameDomSpringValues.y,
            left: frameDomSpringValues.x,
            width: frameDomSpringValues.width,
            height: frameDomSpringValues.height,
            zIndex: stackOrder + STACK_ORDER_GROUPINGS.staticRenderedText
          }}
        >
          <Preview {...props} />
        </animated.svg>
      ) : null}

      {displayState === DISPLAY_STATES.move ? <KeyBindingDelete itemId={data.id} /> : null}

      {[DISPLAY_STATES.move, DISPLAY_STATES.edit].includes(displayState) && containerLocationInDom ? (
        <ControlPanel
          anchorLocation={containerLocationInDom}
          anchorRef={renderPointRef}
          itemData={data}
          stackOrder={stackOrder + STACK_ORDER_GROUPINGS.controlPanel}
          beforeMoveToDrafts={setDisplayStateRender}
        />
      ) : null}

      {!productionRender && containerLocationInDom && displayState === DISPLAY_STATES.render ? (
        <div
          className={classnames('wysiwyg-editor-canvas-element-editindicatorbox', {
            'wysiwyg-editor-canvas-element-editindicatorbox--transparent-active':
              displayState === DISPLAY_STATES.edit || displayState === DISPLAY_STATES.move
          })}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          style={{
            background: 'transparent',
            cursor: 'pointer',
            height: containerLocationInDom.height,
            left: containerLocationInDom.x,
            position: 'absolute',
            top: containerLocationInDom.y,
            width: containerLocationInDom.width,
            zIndex: stackOrder + STACK_ORDER_GROUPINGS.staticRenderedText
          }}
        />
      ) : null}
      {[DISPLAY_STATES.render, DISPLAY_STATES.move, DISPLAY_STATES.moving].includes(displayState) &&
      containerLocationInDom &&
      contentLocationInDom &&
      textOverflows ? (
        <animated.div
          style={{
            position: 'absolute',
            zIndex: stackOrder + STACK_ORDER_GROUPINGS.overflowIndicator,
            top: to(
              [
                frameDomSpringValues.x,
                frameDomSpringValues.y,
                frameDomSpringValues.height,
                frameDomSpringValues.width,
                contentDomSpringValues.x,
                contentDomSpringValues.y,
                contentDomSpringValues.height,
                contentDomSpringValues.width
              ],
              (fX, fY, fH, fW, cX, cY, cH, cW) => {
                return calculateOverflowContainerLocation(
                  overflowIndicatorLocation,
                  { y: fY, x: fX, height: fH, width: fW },
                  { y: cY, x: cX, height: cH, width: cW }
                ).top;
              }
            ),
            left: to(
              [
                frameDomSpringValues.x,
                frameDomSpringValues.y,
                frameDomSpringValues.height,
                frameDomSpringValues.width,
                contentDomSpringValues.x,
                contentDomSpringValues.y,
                contentDomSpringValues.height,
                contentDomSpringValues.width
              ],
              (fX, fY, fH, fW, cX, cY, cH, cW) => {
                return calculateOverflowContainerLocation(
                  overflowIndicatorLocation,
                  { y: fY, x: fX, height: fH, width: fW },
                  { y: cY, x: cX, height: cH, width: cW }
                ).left;
              }
            ),
            height: to(
              [
                frameDomSpringValues.x,
                frameDomSpringValues.y,
                frameDomSpringValues.height,
                frameDomSpringValues.width,
                contentDomSpringValues.x,
                contentDomSpringValues.y,
                contentDomSpringValues.height,
                contentDomSpringValues.width
              ],
              (fX, fY, fH, fW, cX, cY, cH, cW) => {
                return calculateOverflowContainerLocation(
                  overflowIndicatorLocation,
                  { y: fY, x: fX, height: fH, width: fW },
                  { y: cY, x: cX, height: cH, width: cW }
                ).height;
              }
            ),
            width: to(
              [
                frameDomSpringValues.x,
                frameDomSpringValues.y,
                frameDomSpringValues.height,
                frameDomSpringValues.width,
                contentDomSpringValues.x,
                contentDomSpringValues.y,
                contentDomSpringValues.height,
                contentDomSpringValues.width
              ],
              (fX, fY, fH, fW, cX, cY, cH, cW) => {
                return calculateOverflowContainerLocation(
                  overflowIndicatorLocation,
                  { y: fY, x: fX, height: fH, width: fW },
                  { y: cY, x: cX, height: cH, width: cW }
                ).width;
              }
            )
          }}
        >
          <div style={{ position: 'relative' }}>
            <TextOverflowIndicator position={{ top: 0, left: 0 }} show />
          </div>
        </animated.div>
      ) : null}
      {containerLocationInDom &&
      [DISPLAY_STATES.edit, DISPLAY_STATES.resizing, DISPLAY_STATES.move, DISPLAY_STATES.moving].includes(
        displayState
      ) ? (
        <ErrorBoundary
          resetOnChanges={textDocument}
          fallbackComponent={
            <p
              style={{
                background: '#ff0000',
                color: '#ffffff',
                position: 'absolute',
                fontWeight: 'normal',
                top: containerLocationInDom.y + (contentLocationInDom?.y || 0),
                left: containerLocationInDom.x + (contentLocationInDom?.x || 0),
                minHeight: contentLocationInDom?.height ?? containerLocationInDom.height,
                width: contentLocationInDom?.width ?? containerLocationInDom.width,
                cursor: 'default',
                zIndex: stackOrder + STACK_ORDER_GROUPINGS.staticRenderedText
              }}
            >
              There was a problem displaying the text editor. You should either delete this text box or{' '}
              <a
                onClick={() => {
                  setTextDocument(
                    emptyDocument(data.styles?.align, stylesToDefaultMarkValues(defaultMarkValues, data.styles))
                  );
                }}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: '#ffffff',
                  fontWeight: 'bold'
                }}
              >
                click here
              </a>{' '}
              to reset its content.
            </p>
          }
        >
          <TextEditor
            defaultAlignment={data.styles.align || 'center'}
            initialContent={textDocument}
            location={frameDomSpringValues}
            textItem={data}
            localDefaultMarkValues={stylesToDefaultMarkValues(defaultMarkValues, data.styles)}
            isSingleLine={false}
            textOverflows={textOverflows}
            overflowIndicatorPosition={overflowIndicatorLocation}
            focusOnRender={displayState === DISPLAY_STATES.edit}
            onViewMount={handleViewMount}
            zIndex={
              displayState === DISPLAY_STATES.edit
                ? stackOrder + STACK_ORDER_GROUPINGS.activeHandles
                : stackOrder + STACK_ORDER_GROUPINGS.staticRenderedText
            }
          />
        </ErrorBoundary>
      ) : null}
      {!textContent &&
      containerLocationInDom &&
      [DISPLAY_STATES.render, DISPLAY_STATES.move, DISPLAY_STATES.moving, DISPLAY_STATES.resizing].includes(
        displayState
      ) ? (
        <AddTextButton
          location={frameDomSpringValues}
          showBorder={displayState === DISPLAY_STATES.render && !isHovering}
          text={
            displayState === DISPLAY_STATES.render
              ? `${isMobile ? 'Tap' : 'Click'} here to add text`
              : `${isMobile ? 'Tap' : 'Click'} again to add text`
          }
          stackOrder={stackOrder + STACK_ORDER_GROUPINGS.staticRenderedText}
          onClick={setDisplayStateMove}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        />
      ) : null}
      {containerLocationInDom &&
      [DISPLAY_STATES.render, DISPLAY_STATES.move, DISPLAY_STATES.moving].includes(displayState) ? (
        <ContentDragShield
          location={frameDomSpringValues}
          pageItemId={data.id}
          zIndex={stackOrder + STACK_ORDER_GROUPINGS.handles}
          onDrag={handleDrag}
          onDragStart={handleDragStart}
          onDragStop={handleDragStop}
          onPointerEnter={() => setIsHovering(true)}
          onPointerLeave={() => setIsHovering(false)}
        />
      ) : null}
      {containerLocationInDom &&
      ((displayState === DISPLAY_STATES.render && isHovering) ||
        [DISPLAY_STATES.edit, DISPLAY_STATES.move, DISPLAY_STATES.moving, DISPLAY_STATES.resizing].includes(
          displayState
        )) ? (
        <DragFrame
          locations={frameDomSpringValues}
          pageItemId={data.id}
          zIndex={handlesAreActive ? STACK_ORDER_GROUPINGS.activeHandles : STACK_ORDER_GROUPINGS.handles}
          onDrag={handleDrag}
          onDragStart={handleDragStart}
          onDragStop={handleDragStop}
          noHandles={displayState === DISPLAY_STATES.render}
        />
      ) : null}
      {isBeingDragged && containerLocationInDom && snappableLocations ? (
        <SnapLines
          contentItem={data}
          frameLocation={frameViewboxSpringValues}
          snappableLocations={snappableLocations}
        />
      ) : null}
    </Portal>
  );
}

export default Builder;
